import { SOFTWARE_OPTIONS, GAME_OPTIONS } from "./apps-games-catalog";
import { DEGREE_OPTIONS } from "./degree-catalog";

const laptopSilver = "/laptop-silver.jpg";
const laptopDark = "/laptop-dark.jpg";
const laptopWhite = "/laptop-white.jpg";
const laptopRose = "/laptop-rose.jpg";

export type Laptop = {
  id: string;
  name: string;
  brand: string;
  price: number;
  currency: "GBP" | "USD";
  matchScore: number;
  portabilityScore: number;
  futureProofYears: number;
  goodEnough: boolean;
  tagline: string;
  images: string[];
  blurb: string;
  specs: { label: string; value: string; tooltip?: string }[];
  pros: { text: string; source: string }[];
  cons: { text: string; source: string }[];
  userReviewsSummary: string;
  blogReview: { excerpt: string; source: string };
  sellers: { name: string; price: number; url: string }[];
  whyRecommended: { factor: string; weight: number; note: string }[];
};

export const laptops: Laptop[] = [
  {
    id: "zenith-book-pro-14",
    name: "Zenith Book Pro 14",
    brand: "Zenith",
    price: 999,
    currency: "GBP",
    matchScore: 96,
    portabilityScore: 9.2,
    futureProofYears: 5,
    goodEnough: true,
    tagline: "Best overall match",
    images: [laptopSilver, laptopWhite, laptopRose],
    blurb:
      "A featherweight all-rounder that flies through coding, design work and lecture notes, and lasts a full day on battery.",
    specs: [
      { label: "Processor", value: "Apple M3 (8-core)", tooltip: "The chip that runs everything. M3 is fast and very efficient, so battery life is long." },
      { label: "Memory (RAM)", value: "16 GB unified", tooltip: "How much your laptop can think about at once. 16GB is comfortable for student multitasking." },
      { label: "Storage", value: "512 GB SSD" },
      { label: "Display", value: '14.2" Liquid Retina, 120Hz' },
      { label: "Battery", value: "Up to 18 hours" },
      { label: "Weight", value: "1.55 kg" },
      { label: "Ports", value: "3× Thunderbolt, HDMI, SD, Audio jack" },
    ],
    pros: [
      { text: "Best-in-class battery life, easily a full day of lectures", source: "The Verge" },
      { text: "Silent under load, even with heavy IDE workloads", source: "Notebookcheck" },
      { text: "Display is bright enough to use outdoors", source: "PCMag" },
    ],
    cons: [
      { text: "Limited port selection without a dongle", source: "Reddit r/laptops" },
      { text: "Macs can't run some niche Windows-only uni software", source: "Plample analysis" },
    ],
    userReviewsSummary:
      "Across 1,240 user reviews, the picture is consistent: students love the battery and weight, occasionally wish for a touchscreen, and rate it 4.7 / 5 overall.",
    blogReview: {
      excerpt:
        "The most polished student laptop you can buy at this price. It's not the fastest on paper, but it's the most pleasant to live with.",
      source: "Wired",
    },
    sellers: [
      { name: "Amazon UK", price: 999, url: "#" },
      { name: "John Lewis", price: 1019, url: "#" },
      { name: "Currys", price: 1049, url: "#" },
      { name: "Argos", price: 1059, url: "#" },
    ],
    whyRecommended: [
      { factor: "Battery life for long study days", weight: 30, note: "18h easily covers a full day." },
      { factor: "Coding & creative performance", weight: 25, note: "M3 handles VS Code, Figma, Premiere." },
      { factor: "Portability for daily campus carry", weight: 25, note: "1.55kg, barely felt in your bag." },
      { factor: "Stays modern through your degree", weight: 20, note: "5+ years of macOS updates expected." },
    ],
  },
  {
    id: "vantage-air-13",
    name: "Vantage Air 13",
    brand: "Vantage",
    price: 749,
    currency: "GBP",
    matchScore: 87,
    portabilityScore: 8.6,
    futureProofYears: 4,
    goodEnough: true,
    tagline: "Great value",
    images: [laptopWhite, laptopSilver],
    blurb:
      "Almost everything the Zenith offers, £250 cheaper. A small compromise on display and battery, big saving on your budget.",
    specs: [
      { label: "Processor", value: "AMD Ryzen 7 7840U" },
      { label: "Memory (RAM)", value: "16 GB" },
      { label: "Storage", value: "1 TB SSD" },
      { label: "Display", value: '13.3" IPS 60Hz' },
      { label: "Battery", value: "Up to 12 hours" },
      { label: "Weight", value: "1.24 kg" },
      { label: "Ports", value: "2× USB-C, 2× USB-A, HDMI, Audio jack" },
    ],
    pros: [
      { text: "Genuinely light, easy to forget it's in your bag", source: "Tom's Guide" },
      { text: "Twice the storage of the Zenith at this price", source: "Plample analysis" },
    ],
    cons: [
      { text: "Display is fine but not vivid", source: "Notebookcheck" },
      { text: "Battery dips under heavy load", source: "Reddit r/laptops" },
    ],
    userReviewsSummary:
      "863 user reviews average 4.5 / 5. Students mention it's a steal for the storage and weight; build feels plasticky to a few.",
    blogReview: {
      excerpt:
        "If you don't need the polish of more expensive ultrabooks, the Vantage Air 13 is the best value student laptop of the year.",
      source: "TechRadar",
    },
    sellers: [
      { name: "Amazon UK", price: 749, url: "#" },
      { name: "Very", price: 779, url: "#" },
      { name: "Currys", price: 799, url: "#" },
    ],
    whyRecommended: [
      { factor: "Within budget with headroom", weight: 35, note: "Leaves £200 for a monitor or warranty." },
      { factor: "Portability for daily campus carry", weight: 30, note: "1.24kg is featherweight." },
      { factor: "Coding performance", weight: 20, note: "Ryzen 7 handles full-stack dev comfortably." },
      { factor: "Stays modern through your degree", weight: 15, note: "4 years of strong Windows support." },
    ],
  },
  {
    id: "arc-studio-15",
    name: "Arc Studio 15",
    brand: "Arc",
    price: 1199,
    currency: "GBP",
    matchScore: 78,
    portabilityScore: 6.4,
    futureProofYears: 5,
    goodEnough: false,
    tagline: "For creative and gaming sidequests",
    images: [laptopDark, laptopSilver],
    blurb:
      "Heavier and louder, but the dedicated GPU unlocks gaming and video work the others can't touch.",
    specs: [
      { label: "Processor", value: "Intel Core i7-13700H" },
      { label: "Graphics", value: "NVIDIA RTX 4060 (8 GB)" },
      { label: "Memory (RAM)", value: "32 GB" },
      { label: "Storage", value: "1 TB SSD" },
      { label: "Display", value: '15.6" QHD 165Hz' },
      { label: "Battery", value: "Up to 7 hours" },
      { label: "Weight", value: "2.1 kg" },
    ],
    pros: [
      { text: "Runs every modern game at high settings", source: "PC Gamer" },
      { text: "Big, bright high-refresh display for editing", source: "The Verge" },
    ],
    cons: [
      { text: "Heavy, you'll notice it in your bag", source: "Plample analysis" },
      { text: "Fans audible under gaming load", source: "Notebookcheck" },
    ],
    userReviewsSummary:
      "412 reviews averaging 4.3 / 5. Gamers love it, daily commuters wish it were lighter.",
    blogReview: {
      excerpt:
        "A capable gaming machine that doesn't completely sacrifice the productivity essentials.",
      source: "RockPaperShotgun",
    },
    sellers: [
      { name: "Amazon UK", price: 1199, url: "#" },
      { name: "Currys", price: 1199, url: "#" },
      { name: "Scan", price: 1229, url: "#" },
    ],
    whyRecommended: [
      { factor: "Gaming performance", weight: 40, note: "RTX 4060 handles AAA titles." },
      { factor: "Creative software", weight: 30, note: "32GB RAM for Premiere & Blender." },
      { factor: "Future-proofing", weight: 20, note: "Holds up for 5+ years." },
      { factor: "Portability tradeoff", weight: 10, note: "2.1kg is the cost of the GPU." },
    ],
  },
  {
    id: "halo-flex-13",
    name: "Halo Flex 13",
    brand: "Halo",
    price: 649,
    currency: "GBP",
    matchScore: 71,
    portabilityScore: 9.0,
    futureProofYears: 3,
    goodEnough: false,
    tagline: "Budget pick",
    images: [laptopRose, laptopWhite],
    blurb:
      "The cheapest option that still hits your basics. Fine for essays and browsing, lighter on heavy software.",
    specs: [
      { label: "Processor", value: "Intel Core i5-1335U" },
      { label: "Memory (RAM)", value: "8 GB" },
      { label: "Storage", value: "256 GB SSD" },
      { label: "Display", value: '13.3" FHD' },
      { label: "Battery", value: "Up to 10 hours" },
      { label: "Weight", value: "1.30 kg" },
    ],
    pros: [
      { text: "Cheapest in the recommended set by a wide margin", source: "Plample analysis" },
      { text: "Convertible 2-in-1 with touchscreen", source: "Tom's Guide" },
    ],
    cons: [
      { text: "8GB RAM will feel tight by year 3", source: "Plample analysis" },
      { text: "256GB storage fills quickly", source: "Reddit r/laptops" },
    ],
    userReviewsSummary:
      "1,001 reviews averaging 4.2 / 5. Generally happy; power users outgrow it.",
    blogReview: {
      excerpt: "A surprisingly capable budget convertible, just don't expect it to age gracefully.",
      source: "Tom's Guide",
    },
    sellers: [
      { name: "Amazon UK", price: 649, url: "#" },
      { name: "Currys", price: 659, url: "#" },
    ],
    whyRecommended: [
      { factor: "Lowest price in shortlist", weight: 50, note: "£300 cheaper than top pick." },
      { factor: "Portability", weight: 30, note: "Light, convertible." },
      { factor: "Performance compromise", weight: 20, note: "8GB RAM limits long-term use." },
    ],
  },
];

export const recommendationQuestions = [
  {
    id: "country",
    prompt: "Which country are you shopping from?",
    reassurance: "We'll only show you laptops actually available where you are.",
    type: "country" as const,
    options: [
      { value: "gb", label: "United Kingdom" },
      { value: "us", label: "United States" },
    ],
  },
  {
    id: "budget",
    prompt: "What's your max budget for this laptop?",
    reassurance: "Don't worry about being exact. We'll show you a few options around this number.",
    type: "currency" as const,
  },
  {
    // Asked straight after the budget: it is a budget decision, and it is most
    // meaningful once the person has just told us what they can spend.
    id: "refurbished",
    prompt: "Would you consider a refurbished laptop?",
    reassurance:
      "Refurbished machines are tested, graded for condition and come with a warranty — and usually cost 30-50% less than new. We only show graded stock from eBay and Back Market.",
    type: "single" as const,
    options: [
      { value: "no", label: "New only", sub: "Just show me brand-new machines" },
      {
        value: "yes",
        label: "Open to refurbished",
        sub: "Show refurbished options alongside new ones, with their condition grade",
      },
    ],
  },
  {
    id: "software",
    prompt: "Which software will you be using? Pick all that apply.",
    reassurance: "We use this to figure out how much horsepower you actually need.",
    type: "multi-icon" as const,
    options: SOFTWARE_OPTIONS,
  },
  {
    id: "games",
    prompt: "Any games you'd want to play? Skip if none.",
    reassurance: "Gaming changes what hardware you need, but only a bit if it's casual.",
    type: "multi-icon" as const,
    options: GAME_OPTIONS,
  },
  {
    id: "os",
    prompt: "Got a preferred operating system?",
    reassurance: "If you've used macOS or Windows before, sticking with it saves a learning curve.",
    type: "single" as const,
    options: [
      { value: "windows", label: "Windows 11" },
      { value: "mac", label: "macOS" },
      { value: "none", label: "No preference" },
    ],
  },
  {
    id: "screen",
    prompt: "Which screen size feels right?",
    reassurance: "Smaller is lighter for the commute. Bigger is easier on the eyes for long sessions.",
    type: "squircle" as const,
    options: [
      { value: "13", label: '13"', sub: "Ultra-portable" },
      { value: "14", label: '14"', sub: "Sweet spot" },
      { value: "15", label: '15"', sub: "Roomy" },
      { value: "16", label: '16"', sub: "Desktop replacement" },
      { value: "none", label: "No preference", sub: "Surprise me" },
    ],
  },
  {
    id: "brand",
    prompt: "Any brands you'd lean toward?",
    reassurance: "Pick as many as you like, or skip and we'll consider all of them.",
    type: "multi-squircle" as const,
    options: [
      { value: "apple", label: "Apple", sub: "macOS" },
      { value: "dell", label: "Dell", sub: "Windows" },
      { value: "lenovo", label: "Lenovo", sub: "Windows" },
      { value: "hp", label: "HP", sub: "Windows" },
      { value: "asus", label: "ASUS", sub: "Windows" },
      { value: "msi", label: "MSI", sub: "Windows" },
      { value: "aorus", label: "AORUS", sub: "Windows" },
      { value: "gigabyte", label: "Gigabyte", sub: "Windows" },
      { value: "acer", label: "Acer", sub: "Windows" },
      { value: "alienware", label: "Alienware", sub: "Windows" },
    ],
  },
  {
    id: "usage",
    prompt: "Where do you mainly plan to use your laptop?",
    reassurance: "This shapes how we balance raw power against battery life, the two biggest trade-offs in any laptop.",
    type: "single" as const,
    options: [
      { value: "desk", label: "At my desk", sub: "Always near a power outlet, so battery life isn't a priority for me" },
      { value: "hybrid", label: "Desk & on the go", sub: "I move around but can usually find a charger during the day" },
      { value: "mobile", label: "Always on the move", sub: "Full-day battery is a must, I rarely have access to a plug" },
    ],
  },
  {
    id: "features",
    prompt: "Last one. Any must-have features?",
    reassurance: "We pre-ticked the ones most students want. Add or remove as you like.",
    type: "multi-feature" as const,
    options: [
      { value: "has_hdmi", label: "HDMI", defaultChecked: true },
      { value: "has_ethernet", label: "Ethernet", defaultChecked: false },
      { value: "has_mini_display_port", label: "Mini Display Port", defaultChecked: false },
      { value: "has_card_reader", label: "Card Reader", defaultChecked: false },
      { value: "has_finger_print_reader", label: "Finger Print Reader", defaultChecked: false },
      { value: "has_speakers", label: "Speakers", defaultChecked: true },
      // Unlike its neighbours this one is a HARD server-side filter, not a scored
      // preference (see requireTouchscreen in plample-core/algorithm.ts): ticking it
      // removes every laptop we can't confirm has a touch panel. Left unticked by
      // default for exactly that reason — it must be an explicit choice.
      { value: "is_touchscreen", label: "Touchscreen", defaultChecked: false },
    ],
  },
];

// Asked first: it decides how the software question is answered — by picking the
// software directly, or by picking a degree that stands in for its whole toolset.
export const modeQuestion = {
  id: "mode",
  prompt: "How would you like to tell us what you'll be running?",
  reassurance:
    "Pick your degree and we'll fill in its software for you, or choose the software yourself.",
  type: "single" as const,
  options: [
    {
      value: "degree",
      label: "By my degree",
      sub: "We'll pre-select the software typically used on your course",
    },
    {
      value: "software",
      label: "By software",
      sub: "Choose the apps and programs you'll use yourself",
    },
  ],
};

// The degree-based flow swaps the software question for a degree picker; every
// other question (budget, country, games, …) is asked exactly as before.
export const degreeQuestion = {
  id: "degree",
  prompt: "What degree are you studying?",
  reassurance:
    "We'll pre-select every piece of software typically used on your course, so your match reflects the real workload.",
  type: "degree" as const,
  options: DEGREE_OPTIONS,
};

export const degreeRecommendationQuestions = recommendationQuestions.map((q) =>
  q.id === "software" ? degreeQuestion : q,
);
